import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import DateFilter from "@/components/admin/DateFilter";
import { Input } from "@/components/ui/input";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/lang";

const STATUSES = ["all", "pending_payment", "confirmed", "assigned", "in_progress", "completed", "cancelled", "refund_pending", "refunded"];
const STATUS_KEY = {
  all: "filter_all",
  pending_payment: "status_pending_payment",
  confirmed: "status_confirmed",
  assigned: "status_assigned",
  in_progress: "status_in_progress",
  completed: "status_completed",
  cancelled: "status_cancelled",
  refund_pending: "status_refund_pending",
  refunded: "status_refunded",
};

export default function Bookings() {
  const { t, lang } = useLang();
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [assigned, setAssigned] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [dateFilter, setDateFilter] = useState({ preset: "month", from: "", to: "" });
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    if (type !== "all") p.set("type", type);
    if (assigned === "yes") p.set("assigned", "true");
    if (assigned === "no") p.set("assigned", "false");
    if (search) p.set("search", search);
    if (dateFilter.from) p.set("from_date", dateFilter.from);
    if (dateFilter.to) p.set("to_date", dateFilter.to + "T23:59:59");
    p.set("limit", "1000");
    api.get(`/admin/bookings?${p.toString()}`).then((r) => {
      const data = r.data;
      if (Array.isArray(data)) { setBookings(data); setTotal(data.length); }
      else { setBookings(data.items || []); setTotal(data.total || 0); }
    });
  };
  useEffect(load, [status, type, assigned, search, dateFilter.from, dateFilter.to]); // eslint-disable-line

  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow={t("bookings_eyebrow")} title={t("bookings_title")} description={t("bookings_description")}>
        <GoldButton onClick={() => setShowCreate(true)} data-testid="new-booking-btn"><Plus className="w-3.5 h-3.5" /> Nova reserva</GoldButton>
      </PageHeader>

      <div className="mb-4">
        <DateFilter value={dateFilter} onChange={setDateFilter} testId="bookings-date-filter" />
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 border border-slate-300 bg-white px-3 h-9 min-w-[280px]">
          <Search className="w-3.5 h-3.5 text-slate-500" />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchDraft); }}
            placeholder="Ref (DT-…/RT-…), nome, email ou telefone"
            className="flex-1 text-xs border-0 focus:outline-none bg-transparent"
            data-testid="bookings-search"
          />
          {searchDraft && (
            <button onClick={() => { setSearchDraft(""); setSearch(""); }} className="text-slate-400 hover:text-red-600" data-testid="bookings-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <GhostButton type="button" onClick={() => setSearch(searchDraft)} data-testid="bookings-search-btn">Pesquisar</GhostButton>
        </div>
        {total > 0 && (
          <div className="text-[11px] uppercase tracking-widest text-slate-500 self-center" data-testid="bookings-total">
            {bookings.length} de <strong className="text-[#0a0f1e]">{total}</strong>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex flex-wrap gap-1">
          {STATUSES.map((s) => (
            <button key={s} data-testid={`booking-status-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-[10px] uppercase tracking-wide border ${status === s ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500 hover:text-[#0a0f1e]"}`}>{t(STATUS_KEY[s])}</button>
          ))}
        </div>
        <div className="flex gap-1 ml-auto">
          {[["all", t("filter_all")], ["transfer", "Transfer"], ["tour", "Tour"]].map(([k, v]) => (
            <button key={k} onClick={() => setType(k)} className={`px-3 py-1.5 text-[10px] uppercase tracking-wide border ${type === k ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500 hover:text-[#0a0f1e]"}`}>{v}</button>
          ))}
        </div>
        <div className="flex gap-1">
          {[["all", t("filter_any")], ["yes", t("filter_assigned")], ["no", t("filter_unassigned")]].map(([k, v]) => (
            <button key={k} onClick={() => setAssigned(k)} className={`px-3 py-1.5 text-[10px] uppercase tracking-wide border ${assigned === k ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500 hover:text-[#0a0f1e]"}`}>{v}</button>
          ))}
        </div>
      </div>

      <div className="border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 label-caps text-left">
              <th className="p-4">{t("col_ref")}</th>
              <th className="p-4">{t("col_when")}</th>
              <th className="p-4">{t("col_type")}</th>
              <th className="p-4">{t("col_client")}</th>
              <th className="p-4">{t("col_route")}</th>
              <th className="p-4">{t("col_driver")}</th>
              <th className="p-4 text-right">{t("col_amount")}</th>
              <th className="p-4">{t("col_payment")}</th>
              <th className="p-4">{t("col_status")}</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 && (<tr><td colSpan={10} className="p-10 text-center text-slate-500">{t("bookings_none")}</td></tr>)}
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="p-4 font-mono text-[#f59e0b] text-xs">{b.reference}</td>
                <td className="p-4 text-xs text-slate-700">{new Date(b.service_datetime).toLocaleString(lang.toLowerCase() === "pt" ? "pt-PT" : lang.toLowerCase(), { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="p-4 text-xs uppercase text-slate-500">{b.type}</td>
                <td className="p-4">
                  <div className="text-[#0a0f1e] text-sm">{b.client_name}</div>
                  <div className="text-xs text-slate-500">{b.client_phone}</div>
                </td>
                <td className="p-4 text-xs text-slate-500 max-w-[240px]">
                  <div className="truncate">{b.pickup_address}</div>
                  <div className="truncate text-slate-500">→ {b.destination_address}</div>
                </td>
                <td className="p-4 text-xs text-slate-500">{b.driver_id ? t("filter_assigned") : <span className="text-amber-400">{t("filter_unassigned")}</span>}</td>
                <td className="p-4 text-right text-[#0a0f1e] font-mono">€{b.total_amount?.toFixed(2)}</td>
                <td className="p-4"><StatBadge status={b.payment_status} /></td>
                <td className="p-4"><StatBadge status={b.status} /></td>
                <td className="p-4"><Link to={`/admin/bookings/${b.id}`} className="text-xs uppercase text-[#f59e0b] hover:underline">{t("action_open")}</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <NewBookingModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function NewBookingModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    type: "transfer",
    client_name: "",
    client_email: "",
    client_phone: "",
    pickup_address: "",
    destination_address: "",
    service_datetime: new Date().toISOString().slice(0, 16),
    service_duration_hours: 1,
    passengers: 1,
    luggage: 0,
    flight_number: "",
    admin_notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...f,
        passengers: Number(f.passengers) || 1,
        luggage: Number(f.luggage) || 0,
        service_duration_hours: Number(f.service_duration_hours) || 1,
        service_datetime: new Date(f.service_datetime).toISOString(),
      };
      await api.post("/admin/bookings", payload);
      toast.success("Reserva criada");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="new-booking-modal">
      <div className="bg-white border border-[#f59e0b]/30 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl text-[#0a0f1e]">Nova reserva (manual)</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-[#0a0f1e]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Fld l="Tipo">
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full h-10 px-3 border border-slate-300 bg-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="new-type">
                <option value="transfer">Transfer</option>
                <option value="tour">Tour</option>
              </select>
            </Fld>
            <Fld l="Data / hora"><Input type="datetime-local" value={f.service_datetime} onChange={(e) => setF({ ...f, service_datetime: e.target.value })} required data-testid="new-datetime" /></Fld>
            <Fld l="Duração (h)"><Input type="number" min="0.5" step="0.5" value={f.service_duration_hours} onChange={(e) => setF({ ...f, service_duration_hours: e.target.value })} required data-testid="new-duration" /></Fld>
          </div>
          <Fld l="Nome do cliente"><Input value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} required data-testid="new-client-name" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld l="Email"><Input type="email" value={f.client_email} onChange={(e) => setF({ ...f, client_email: e.target.value })} required data-testid="new-client-email" /></Fld>
            <Fld l="Telemóvel"><Input value={f.client_phone} onChange={(e) => setF({ ...f, client_phone: e.target.value })} required data-testid="new-client-phone" /></Fld>
          </div>
          <Fld l="Endereço de recolha"><Input value={f.pickup_address} onChange={(e) => setF({ ...f, pickup_address: e.target.value })} required data-testid="new-pickup" /></Fld>
          <Fld l="Endereço de destino"><Input value={f.destination_address} onChange={(e) => setF({ ...f, destination_address: e.target.value })} required data-testid="new-destination" /></Fld>
          <div className="grid grid-cols-3 gap-3">
            <Fld l="Passageiros"><Input type="number" min="1" value={f.passengers} onChange={(e) => setF({ ...f, passengers: e.target.value })} data-testid="new-passengers" /></Fld>
            <Fld l="Malas"><Input type="number" min="0" value={f.luggage} onChange={(e) => setF({ ...f, luggage: e.target.value })} data-testid="new-luggage" /></Fld>
            <Fld l="Voo (opcional)"><Input value={f.flight_number} onChange={(e) => setF({ ...f, flight_number: e.target.value })} data-testid="new-flight" /></Fld>
          </div>
          <Fld l="Notas internas"><Input value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} placeholder="Recepção — pedido por telefone…" data-testid="new-notes" /></Fld>
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 p-2">
            O preço é calculado automaticamente a partir dos endereços. Poderá editar todos os valores depois de criar a reserva.
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <GhostButton type="button" onClick={onClose}>Cancelar</GhostButton>
            <GoldButton type="submit" disabled={saving} data-testid="save-new-booking">{saving ? "A criar…" : "Criar reserva"}</GoldButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function Fld({ l, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">{l}</label>
      {children}
    </div>
  );
}
